import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getListBoxPropsAutoScrolItem, renderOptionsKtree } from 'utils/Object';
import {
  DimensionalViewRow,
  RowMemberOption,
  filterRowsByMember,
  getRowMemberFilterOptions,
  isMemberDimension,
} from '../utils/rowMemberFilter';

export interface RowMemberFilterProps {
  label: string;
  options: RowMemberOption[];
  value: RowMemberOption;
  onChange: (option: RowMemberOption) => void;
  disabled?: boolean;
}

export type RowMemberFilterState = Omit<RowMemberFilterProps, 'label' | 'disabled'>;

export function RowMemberFilter(props: Readonly<RowMemberFilterProps>) {
  const { label, options, value, onChange, disabled } = props;

  const lowestLevel = useMemo(() => Math.max(...options.map((option) => option.level)), [options]);

  return (
    <Autocomplete
      onChange={(event, option) => onChange(option)}
      disableClearable
      size='small'
      sx={{ width: 300 }}
      value={value}
      disabled={disabled}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      renderOption={(optionProps, option, state) => renderOptionsKtree(optionProps, option, state, lowestLevel)}
      ListboxProps={{ ...getListBoxPropsAutoScrolItem(options, value) }}
      renderInput={(params) => <TextField {...params} label={label} name='rowMember' />}
    />
  );
}

// Narrows the grid rows to one KTree member and its descendants when the rows (Down) are a KTree dimension
export function useRowMemberFilter<T extends DimensionalViewRow>(rows: T[], rowDimensionType?: string) {
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const isMemberRows = isMemberDimension(rowDimensionType);

  // A member of one dimension means nothing in another, so start over when Down changes
  useEffect(() => {
    setSelectedMemberId(null);
  }, [rowDimensionType]);

  const options = useMemo(() => (isMemberRows ? getRowMemberFilterOptions(rows) : []), [rows, isMemberRows]);
  const selectedOption = options.find((option) => option.value === selectedMemberId) ?? options[0];

  const filteredRows = useMemo(
    () => filterRowsByMember(rows, selectedOption?.value ?? null),
    [rows, selectedOption?.value]
  );

  const rowMemberFilter: RowMemberFilterState | undefined = selectedOption && {
    options,
    value: selectedOption,
    onChange: (option) => setSelectedMemberId(option.value),
  };

  return { filteredRows, rowMemberFilter };
}
